import React, { useState } from 'react';
import { useDropzone } from 'react-dropzone';
import FileViewer from './FileViewer';

const AttachmentUploader = ({ files, setFiles, maxFiles = 5, maxSize = 10485760 }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [isFileViewerOpen, setIsFileViewerOpen] = useState(false);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'image/png': ['.png'],
      'image/jpeg': ['.jpg', '.jpeg'],
      'application/pdf': ['.pdf']
    },
    maxSize,
    onDrop: (acceptedFiles) => {
      const newFiles = acceptedFiles.map(file =>
        Object.assign(file, {
          preview: URL.createObjectURL(file)
        })
      );
      setFiles(prevFiles => [...prevFiles, ...newFiles].slice(0, maxFiles));
    }
  });

  const removeFile = (fileToRemove) => {
    setFiles(files.filter(file => file !== fileToRemove));
    // Clean up the preview URL to prevent memory leaks
    if (fileToRemove.preview) {
      URL.revokeObjectURL(fileToRemove.preview);
    }
  };

  const handleFileView = (file) => {
    setSelectedFile({
      filename: file.name,
      url: file.preview,
      size: file.size
    });
    setIsFileViewerOpen(true);
  };

  const getFileIcon = (filename) => {
    const extension = filename?.toLowerCase().split('.').pop();
    if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(extension)) {
      return '🖼️';
    } else if (extension === 'pdf') {
      return '📄';
    }
    return '📎';
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="space-y-4">
      {/* Upload Area */}
      <div
        {...getRootProps()}
        className={`border-2 border-dashed rounded-md p-6 cursor-pointer transition-colors ${
          isDragActive
            ? 'border-blue-400 bg-blue-50'
            : 'border-primary hover:bg-gray-50'
        }`}
      >
        <input {...getInputProps()} />
        <div className="text-center">
          <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
            <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <p className="mt-2 text-sm text-gray-600">
            {isDragActive ? 'Drop files here...' : 'Drag and drop files here, or click to select files'}
          </p>
          <p className="text-xs text-gray-500 mt-1">
            Max {formatFileSize(maxSize)} per file. Supported formats: JPG, PNG, PDF
          </p>
        </div>
      </div>

      {/* Uploaded Files List */}
      {files.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-gray-900">
            Uploaded Files ({files.length}/{maxFiles})
          </h4>

          <div className="space-y-2">
            {files.map((file, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border">
                <div className="flex items-center space-x-3 flex-1 min-w-0">
                  <span className="text-lg">{getFileIcon(file.name)}</span>
                  <div className="flex-1 min-w-0">
                    <button
                      type="button"
                      onClick={() => handleFileView(file)}
                      className="text-blue-600 hover:text-blue-800 font-medium text-left truncate block w-full"
                    >
                      {file.name}
                    </button>
                    <p className="text-xs text-gray-500">
                      {formatFileSize(file.size)}
                    </p>
                  </div>
                </div>

                <div className="flex items-center space-x-2 ml-2">
                  <button
                    type="button"
                    onClick={() => handleFileView(file)}
                    className="text-blue-600 hover:text-blue-800 p-1"
                    title="Preview file"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  </button>

                  <button
                    type="button"
                    onClick={() => removeFile(file)}
                    className="text-red-600 hover:text-red-800 p-1"
                    title="Remove file"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>
            ))}
          </div>

          {files.length >= maxFiles && (
            <p className="text-sm text-orange-600">
              Maximum number of files reached. Remove a file to add another.
            </p>
          )}
        </div>
      )}

      {/* File Viewer Modal */}
      <FileViewer
        file={selectedFile}
        isOpen={isFileViewerOpen}
        onClose={() => {
          setIsFileViewerOpen(false);
          setSelectedFile(null);
        }}
      />
    </div>
  );
};

export default AttachmentUploader;
